import { useState } from 'react';
import { Badge, EmptyState, Loading, StatCard, useAsync } from '../../components/ui';
import { IconAlert, IconBox, IconCheck, IconClock, IconFlash } from '../../components/icons';
import { api } from '../../lib/api';
import { formatCurrency, formatNumber, formatRelative } from '../../lib/format';
import { printList, printQuantity } from '../../lib/print';

type Group = 'below' | 'negative' | 'over' | 'stale' | 'empty';

/**
 * Ausdruck der Bestandskontrolle.
 *
 * Alle fünf Prüfungen kommen in eine Tabelle mit vorangestellter Kategorie –
 * fünf Einzeltabellen ergäben ein Blatt, auf dem man ständig die Spaltenköpfe
 * neu lesen muss. Die Reihenfolge folgt der Dringlichkeit: Negativbestände
 * zuerst.
 */
function printControl(findings: Record<string, any[]>, byWarehouse: any[], summary: any, staleDays: number) {
  const rows = GROUPS.flatMap((group) =>
    (findings[group.key] ?? []).map((row: any) => ({ ...row, groupKey: group.key, groupLabel: group.label })),
  );

  printList({
    title: 'Bestandskontrolle',
    subtitle: 'Auffälligkeiten im Artikelbestand',
    orientation: 'landscape',
    meta: [
      { label: 'Bestandswert', value: formatCurrency(summary.totalValue) },
      { label: 'Geführte Artikel', value: String(summary.articles) },
      { label: 'Ohne Bewegung seit', value: `${staleDays} Tagen` },
      { label: 'Auffälligkeiten', value: String(rows.length) },
    ],
    columns: [
      { label: 'Kategorie', value: (row: any) => row.groupLabel, emphasis: (row: any) => (row.groupKey === 'negative' ? 'danger' : row.groupKey === 'below' ? 'warning' : null) },
      { label: 'Nummer', value: (row: any) => row.article_no },
      { label: 'Artikel', value: (row: any) => row.name },
      { label: 'Bestand', value: (row: any) => printQuantity(row.quantity, row.unit), align: 'right' },
      { label: 'Mindest', value: (row: any) => formatNumber(row.min_stock, 2), align: 'right' },
      { label: 'Soll', value: (row: any) => formatNumber(row.target_stock, 2), align: 'right' },
      { label: 'Wert', value: (row: any) => formatCurrency(row.value), align: 'right' },
      { label: 'Letzte Bewegung', value: (row: any) => (row.last_movement ? formatRelative(row.last_movement) : 'nie') },
    ],
    rows,
    note: byWarehouse.length
      ? `Bestandswert je Lager: ${byWarehouse
          .map((warehouse: any) => `${warehouse.name} ${formatCurrency(warehouse.value)}`)
          .join('  ·  ')}`
      : undefined,
  });
}

const GROUPS: { key: Group; label: string; hint: string; tone: 'danger' | 'warning' | 'info' | 'accent' }[] = [
  {
    key: 'negative',
    label: 'Negativbestand',
    hint: 'Rechnerisch unmöglich – hier wurde entnommen, was nie eingebucht war.',
    tone: 'danger',
  },
  {
    key: 'below',
    label: 'Unter Mindestbestand',
    hint: 'Nachbestellung fällig, sonst steht der nächste Einsatz ohne Material da.',
    tone: 'warning',
  },
  {
    key: 'over',
    label: 'Deutlich über Sollbestand',
    hint: 'Mehr als das Doppelte des Solls – gebundenes Kapital im Regal.',
    tone: 'info',
  },
  {
    key: 'stale',
    label: 'Ohne Bewegung',
    hint: 'Bestand vorhanden, aber lange nicht angefasst.',
    tone: 'accent',
  },
  {
    key: 'empty',
    label: 'Ohne Bestand',
    hint: 'Geführt, aber nirgends vorrätig und ohne Mindestbestand.',
    tone: 'info',
  },
];

/**
 * Bestandskontrolle (Kap. 7).
 *
 * Fünf Prüfungen, die im Lageralltag tatsächlich wehtun – statt einer
 * Bestandsliste, in der man die Auffälligkeiten selbst suchen muss. Der
 * Negativbestand steht bewusst obenan: Er ist kein Engpass, sondern ein
 * Buchungsfehler und wird sonst übersehen.
 */
export function StockControlPanel() {
  const [staleDays, setStaleDays] = useState(180);
  const [openGroup, setOpenGroup] = useState<Group | null>('below');

  const control = useAsync(() => api.get('/api/stocktaking/control', { staleDays }), [staleDays]);

  if (control.loading) return <Loading />;
  if (!control.data) return <EmptyState title="Keine Bestandsdaten" />;

  const { summary, findings, byWarehouse } = control.data;

  return (
    <div className="stack">
      <div className="grid cols-4">
        <StatCard
          label="Bestandswert"
          value={formatCurrency(summary.totalValue)}
          tone="success"
          icon={<IconBox width={17} height={17} />}
          hint={`${summary.articles} geführte Artikel`}
        />
        <StatCard
          label="Unter Mindestbestand"
          value={summary.below}
          tone={summary.below > 0 ? 'warning' : 'success'}
          icon={<IconAlert width={17} height={17} />}
          emphasis={summary.below > 0 ? 'attention' : undefined}
          hint="Nachbestellung fällig"
        />
        <StatCard
          label="Negativbestand"
          value={summary.negative}
          tone={summary.negative > 0 ? 'danger' : 'success'}
          icon={<IconFlash width={17} height={17} />}
          emphasis={summary.negative > 0 ? 'alarm' : undefined}
          hint={summary.negative > 0 ? 'Buchungsfehler prüfen' : 'keine Auffälligkeit'}
        />
        <StatCard
          label="Ohne Bewegung"
          value={summary.stale}
          tone="accent"
          icon={<IconClock width={17} height={17} />}
          hint={`seit über ${staleDays} Tagen`}
        />
      </div>

      <div className="row between">
        <span className="section-title">Auffälligkeiten</span>
        <div className="row" style={{ gap: 8 }}>
          <label className="toolbar-field">
            <span>Ohne Bewegung seit</span>
            <select value={staleDays} onChange={(event) => setStaleDays(Number(event.target.value))}>
              <option value={90}>90 Tagen</option>
              <option value={180}>180 Tagen</option>
              <option value={365}>1 Jahr</option>
            </select>
          </label>
          <button className="btn sm" onClick={() => printControl(findings, byWarehouse ?? [], summary, staleDays)}>
            Liste drucken
          </button>
        </div>
      </div>

      {GROUPS.map((group) => {
        const rows: any[] = findings[group.key] ?? [];
        const open = openGroup === group.key;
        return (
          <div key={group.key} className={`order-card plan-tile tone-${group.tone}`}>
            <button className="plan-tile-head" onClick={() => setOpenGroup(open ? null : group.key)} aria-expanded={open}>
              <span style={{ transform: open ? 'none' : 'rotate(-90deg)', transition: 'transform 150ms' }}>▾</span>
              <div className="plan-tile-main">
                <div className="row" style={{ gap: 8 }}>
                  <strong>{group.label}</strong>
                  <Badge variant={rows.length === 0 ? 'success' : group.tone === 'danger' ? 'danger' : 'warning'}>
                    {rows.length}
                  </Badge>
                </div>
                <span className="small subtle">{group.hint}</span>
              </div>
            </button>

            {open && (
              <div className="plan-tile-body">
                {rows.length === 0 ? (
                  <span className="small subtle">
                    <IconCheck width={13} height={13} /> Keine Auffälligkeit in dieser Kategorie.
                  </span>
                ) : (
                  <div className="table-wrap">
                    <table className="data">
                      <thead>
                        <tr>
                          <th>Artikel</th>
                          <th className="num">Bestand</th>
                          <th className="num">Mindest</th>
                          <th className="num">Soll</th>
                          <th className="num">Wert</th>
                          <th>Letzte Bewegung</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row) => (
                          <tr key={row.id}>
                            <td>
                              <strong>{row.name}</strong>
                              <div className="small subtle mono">{row.article_no}</div>
                            </td>
                            <td className="num">
                              <strong style={row.quantity < 0 ? { color: 'var(--danger-text)' } : undefined}>
                                {formatNumber(row.quantity, 2)}
                              </strong>{' '}
                              <span className="small subtle">{row.unit}</span>
                            </td>
                            <td className="num small">{formatNumber(row.min_stock, 2)}</td>
                            <td className="num small">{formatNumber(row.target_stock, 2)}</td>
                            <td className="num small">{formatCurrency(row.value)}</td>
                            <td className="small subtle">
                              {row.last_movement ? formatRelative(row.last_movement) : 'nie'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      <span className="section-title">Bestandswert je Lager</span>
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Lager</th>
              <th className="num">Artikel mit Bestand</th>
              <th className="num">Wert</th>
              <th>Anteil</th>
            </tr>
          </thead>
          <tbody>
            {(byWarehouse ?? []).map((warehouse: any) => (
              <tr key={warehouse.id}>
                <td>
                  <strong>{warehouse.name}</strong>
                </td>
                <td className="num">{warehouse.article_count}</td>
                <td className="num">{formatCurrency(warehouse.value)}</td>
                <td style={{ minWidth: 160 }}>
                  <span className="meter">
                    <span
                      className="meter-fill"
                      style={{ width: `${summary.totalValue > 0 ? (warehouse.value / summary.totalValue) * 100 : 0}%` }}
                    />
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
